import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useTheme } from '@mui/material/styles';
import { format } from 'date-fns';

const RevenueBySourceChart = ({ data }) => {
  const theme = useTheme();
  
  // Process the data for the chart
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    // Group by month and then transform for the stacked bar chart
    const groupedByMonth = data.reduce((acc, item) => {
      const month = item.month;
      if (!acc[month]) {
        acc[month] = {
          month,
          solar: 0,
          wind: 0,
          hydro: 0,
          fossil: 0,
        };
      }
      
      acc[month][item.energy_source] = item.total_revenue;
      return acc;
    }, {});
    
    // Convert to array and sort by month
    return Object.values(groupedByMonth).sort((a, b) => a.month.localeCompare(b.month));
  }, [data]);
  
  // Custom tooltip formatter
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ 
          backgroundColor: '#fff', 
          padding: '10px', 
          border: '1px solid #ccc',
          borderRadius: '4px'
        }}>
          <p style={{ margin: 0, fontWeight: 'bold' }}>{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ margin: '5px 0', color: entry.color }}>
              {entry.name}: ${entry.value.toLocaleString()}
            </p>
          ))}
          <p style={{ margin: '5px 0', fontWeight: 'bold' }}>
            Total: ${payload.reduce((sum, entry) => sum + entry.value, 0).toLocaleString()}
          </p>
        </div>
      );
    }
    return null;
  };

  // Format month for display
  const formatMonth = (month) => {
    const [year, monthNum] = month.split('-');
    return format(new Date(parseInt(year), parseInt(monthNum) - 1), 'MMM yyyy');
  };

  return (
    <ResponsiveContainer width="100%" height={400}>
      <BarChart
        data={chartData}
        margin={{ top: 20, right: 30, left: 40, bottom: 100 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis 
          dataKey="month" 
          tickFormatter={formatMonth}
          angle={-60}
          textAnchor="end"
          height={120}
          dy={20}
          tick={{
            fontSize: 16,
            fill: theme.palette.text.secondary
          }}
          interval={0}
        />
        <YAxis 
          tickFormatter={(value) => `$${value / 1000}k`} 
          label={{ value: 'Revenue ($)', angle: -90, position: 'insideLeft', offset: -30 }}
          tick={{
            fontSize: 16,
            dx: -5
          }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend />
        <Bar 
          dataKey="solar" 
          name="Solar" 
          stackId="a" 
          fill={theme.palette.energy.solar} 
        />
        <Bar 
          dataKey="wind" 
          name="Wind" 
          stackId="a" 
          fill={theme.palette.energy.wind} 
        />
        <Bar 
          dataKey="hydro" 
          name="Hydro" 
          stackId="a" 
          fill={theme.palette.energy.hydro} 
        />
        <Bar 
          dataKey="fossil" 
          name="Fossil" 
          stackId="a" 
          fill={theme.palette.energy.fossil} 
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default RevenueBySourceChart;